/**
 * CallStatus.js
 * Talkroute
 *
 * Created by Vel Genov on 12/30/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useMemo } from "react";
import { connect } from "react-redux";
import { Button } from "semantic-ui-react";

import { domIds } from "@constants/testConstants";

import RightSideModal from "./modal/RightSideModal";
import { hangUp, setIsMuteMic, accept } from "../actions/callActions";
import { formatPhoneNumber } from "../services/dataClasses/CallData";
import uiString from "../constants/uiString";
import { CallStateEnum, CallTypeEnum } from "../constants/callConstants";
import AnswerIcon from "@resources/icons/dialer-answer.svg";
import HangupIcon from "@resources/icons/call-status-end.svg";
import MuteIcon from "@resources/icons/call-status-mute.svg";
import UnmuteIcon from "@resources/icons/call-status-mute-active.svg";
import { getHHMMSSTime } from "@helpers/dateFormatter";
import { FilePathHelper } from "../helpers";
import ipcConstants from "@constants/ipcConstants";
import appConfig from "../config";
import { IconButton } from "@material-ui/core";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { GET_TEN_THOUSAND_CONTACTS, CONTACTS_INITIAL_PAGE } from "./Contacts/constants";
import { findFirstContactByNumberFromList } from "@helpers/functions/contacts";

const iconStyle = {width: 18, height: 18};
interface ICallStatusProps {
  callNumber: string;
  incomingCallNumber: string;
  dialedNumber: string;
  numberToCall: string;
  connectedTime: number;
  callState: CallStateEnum;
  isMuteMic: boolean;
  callType: string;
  selectedCallNotificationSound: number;
  hangUp: () => any;
  setIsMuteMic: (value: boolean) => any;
  accept: () => any;
}


const CallControlWidget = ({number, index, status, Answer, Mute, Hangup, ConfirmationModal}: {
  number?: string,
  index?: number,
  status?: string,
  Answer?: React.FC,
  Mute?: React.FC,
  Hangup?: React.FC,
  ConfirmationModal?: (callNumber: string) => JSX.Element,
}) => {
 const formattedPhoneNumber = number ? formatPhoneNumber(number) : uiString.calls.UNKNOWN;
 const {data: _contacts, } = useGetAllContactsQuery({ params: { 
  pageSize: GET_TEN_THOUSAND_CONTACTS,
  page: CONTACTS_INITIAL_PAGE
} });

const contacts = useMemo(() => _contacts || [], [_contacts]);
const contact = useMemo(() => number ? findFirstContactByNumberFromList(number, contacts) : null, [number, contacts]);
const callerInformation = contact ? contact.name : formattedPhoneNumber
 let statusString = status;
 if (index) {
   switch (index % 4) {
     case 1:
       statusString += " .";
       break;
     case 2:
       statusString += " ..";
       break;
     case 3:
       statusString += " ...";
       break;
     default:
       break;
   }
 }

 return (
   <div style={{ paddingLeft: "35px" }}>
     <div
       className="menu-text-normal"
       style={{ display: "flex", alignItems: "center" }}
     >
       <div style={{ lineHeight: "18px" }} className="none-select-text">
         {callerInformation}
       </div>
       <div>{Hangup && <Hangup />}</div>
       <div>{Mute && <Mute />}</div>
       <div>{Answer && <Answer />}</div>
     </div>
     {status && <div className="text-normal none-select-text">{statusString}</div>}
     {ConfirmationModal && ConfirmationModal(formattedPhoneNumber)}
   </div>
 );
};
/**
 * Shows the status of an in progress call
 */
class CallStatus extends React.Component<ICallStatusProps, any> {
  public ipcRenderer = appConfig.electron?.ipcRenderer;
  public audioPlayer;
  public timer;

  static defaultProps = {
    callState: CallStateEnum.IDLE,
    isMuteMic: false,
    callType: CallTypeEnum.OUTGOING,
    selectedCallNotificationSound: 0,
  };

  /**
   * Hang up the current call
   */
  hangUp = () => {
    const { hangUp: hangUpLocal } = this.props;

    this.setState({ isShowConfirmEndCall: false });
    hangUpLocal();
  };

  /**
   * Handle mute or not for current call
   */
  handleMute = () => {
    const { isMuteMic, setIsMuteMic: setIsMuteMicLocal } = this.props;

    setIsMuteMicLocal(!isMuteMic);
  };

  /**
   * Handle hanging up the current call
   */
  handleHangup = () => {
    this.setState({ isShowConfirmEndCall: true });
  };

  /**
   * Handle answering the incoming call
   */
  handleAnswer = () => {
    const { accept: acceptLocal } = this.props;
    acceptLocal();
  };

  /**
   * Display the hang up button for the current call
   */
  showHangupButton = () => (
    <IconButton
      id={domIds.CALL_STATUS_HANGUP_BUTTON}
      onClick={this.handleHangup}
      style={{
        boxShadow: "none",
        marginLeft: "10px",
        padding: 0,
      }}
    >
      <HangupIcon style={iconStyle} />
    </IconButton>
  );

  /**
   * Display the answer button for the incoming call
   */
  showAnswerButton = () => (
    <IconButton
      id={domIds.CALL_STATUS_ANSWER_BUTTON}
      onClick={this.handleAnswer}
      style={{
        boxShadow: "none",
        marginLeft: "4px",
        padding: 0,
      }}
    >
      <AnswerIcon style={iconStyle}/>
    </IconButton>
  );

  /**
   * Display the mute button for the current call
   */
  showMuteButton = () => {
    const { isMuteMic } = this.props;

    return (
      <IconButton
        id={domIds.CALL_STATUS_MUTE_BUTTON}
        onClick={this.handleMute}
        style={{
          boxShadow: "none",
          marginLeft: "4px",
          padding: 0,
        }}
      >
        {isMuteMic ? <UnmuteIcon style={iconStyle}/> : <MuteIcon style={iconStyle}/>}
      </IconButton>
    );
  };

  /**
   * Display the current call status, and enable/disable the hangup button based on it
   *
   * @param callNumber if the user is currently on a call, this is the number the are connected to
   */
  showCallStatus = () => {
    const { callState, incomingCallNumber } = this.props;

    switch (callState) {
      case CallStateEnum.IDLE:
        return this.showIdleWidget();
      case CallStateEnum.CONNECTING:
        return this.showConnectingWidget();
      case CallStateEnum.INCOMING:
        return this.showIncomingWidget();
      case CallStateEnum.ACCEPT:
        return this.showConnectedWidget();
      case CallStateEnum.CONNECTED:
        return this.showConnectedWidget();
      default:
        return this.showIdleWidget();
    }
  };

  /**
   * Set the style to "idle"
   */
  showIdleWidget = () => null;

  /**
   * Shows end call confirm modal
   *
   * @param callNumber The number on calling
   */
  showConfirmEndCallModal = (callNumber, incomingCall?) => {
    const { isShowConfirmEndCall } = this.state;
    const descStr = incomingCall
      ? `${uiString.confirmModal.declineCall.DESC} ${callNumber}.`
      : `${uiString.confirmModal.endCall.DESC} ${callNumber}.`;

    const title = incomingCall
      ? uiString.confirmModal.declineCall.TITLE
      : uiString.confirmModal.endCall.TITLE;

    if (isShowConfirmEndCall) {
      return (
        <RightSideModal id={domIds.CALL_STATUS_HANGUP_CONFIRMATION_DIALOG}>
          <div
            className="text-large"
            style={{
              borderBottom: "1px solid var(--color-dark-grey)",
              textAlign: "center",
              fontWeight: 700,
              padding: "20px 0 15px 0",
            }}
          >
            {title}
          </div>
          <div
            className="text-large"
            style={{ padding: "15px 15px 0 15px", textAlign: "center" }}
          >
            {descStr}
          </div>
          <div
            className="text-normal"
            style={{
              padding: "15px 15px 15px 15px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              id={domIds.CALL_STATUS_HANGUP_CONFIRMATION_DIALOG_CANCEL}
              basic
              icon
              onClick={() => this.setState({ isShowConfirmEndCall: false })}
              style={{ margin: "0" }}
            >
              {uiString.confirmModal.CANCEL}
            </Button>
            <Button
              id={domIds.CALL_STATUS_HANGUP_CONFIRMATION_DIALOG_OK}
              basic
              icon
              onClick={() => this.hangUp()}
              style={{ margin: "0" }}
              className="dev-button"
            >
              {uiString.confirmModal.HANGUP}
            </Button>
          </div>
        </RightSideModal>
      );
    }

    return <></>;
  };

  /**
   * Set the style to "connecting"
   */
  showConnectingWidget = () => {
    return <CallControlWidget 
      number={this.props.numberToCall}
      index={this.state.index}
      status={uiString.calls.CALLING}
      Hangup={this.showHangupButton}
      Mute={this.showMuteButton}
      ConfirmationModal={this.showConfirmEndCallModal}
      />
  };

  /**
   * Set the style to "incoming"
   */
  showIncomingWidget = () => {
    return <CallControlWidget
      number={this.props.incomingCallNumber}
      index={this.state.index}
      status={uiString.calls.INCOMING}
      Hangup={this.showHangupButton}
      Answer={this.showAnswerButton}
      ConfirmationModal={(number) => this.showConfirmEndCallModal(number, true)}
    />
  };

  /**
   * Set the style to "connected"
   */
  showConnectedWidget = () => {
    const {
      callNumber,
      incomingCallNumber,
      callType,
      connectedTime,
    } = this.props;
    let phoneNumber;

    switch (callType) {
      case CallTypeEnum.OUTGOING:
        phoneNumber = callNumber;
        break;
      case CallTypeEnum.INCOMING:
        phoneNumber = incomingCallNumber;
        break;
      default:
        phoneNumber = callNumber;
    }

    let formattedPhoneNumber = uiString.calls.UNKNOWN;
    if (phoneNumber) {
      formattedPhoneNumber = formatPhoneNumber(phoneNumber);
    }

    const { timestamp } = this.state;
    const diffTimestamp =
      timestamp - connectedTime < 0 ? 0 : timestamp - connectedTime;
    const statusString = getHHMMSSTime(diffTimestamp);

    return <CallControlWidget 
      number={phoneNumber ||  "18152055848"}
      status={statusString}
      Hangup={this.showHangupButton}
      Mute={this.showMuteButton}
      ConfirmationModal={this.showConfirmEndCallModal}
    />
  };

  /**
   * Sets the index to 0
   */
  clearIndex = () => {
    this.setState({ index: 0 });
  };

  /**
   * Increases the index
   */
  increaseIndex = () => {
    const { index } = this.state;

    this.setState({ index: index + 1 });
  };

  /**
   * Set the timestamp to current timestamp
   */
  setTimestamp = () => {
    const curTimestamp = Date.now();

    this.setState({ timestamp: curTimestamp });
  };

  /**
   * Create the audio player and set up an input file
   */
  setupAudioPlayer = () => {
    const { selectedCallNotificationSound } = this.props;
    let filePath = "";

    if (selectedCallNotificationSound !== -1) {
      filePath = new FilePathHelper().getFullFilePath(
        `sounds/${uiString.CALL_NOTIFICATION_SOUND_FILES[selectedCallNotificationSound]}`
      );
    }

    this.audioPlayer = new Audio();
    this.audioPlayer.src = filePath;
    this.audioPlayer.loop = true;
  };

  constructor(props) {
    super(props);

    this.state = {
      index: 0,
      timestamp: null,
      isShowConfirmEndCall: false,
    };

    this.timer = null;
  }

  /**
   * Lifecycle function, called when the component is mounted to do additional setup
   */
  componentDidMount() {
    this.setupAudioPlayer();
  }

  componentDidUpdate(prevProps) {
    const {
      callState: prevCallState,
      selectedCallNotificationSound: prevSelectedCallNotificationSound,
    } = prevProps;
    const { callState, selectedCallNotificationSound, incomingCallNumber, dialedNumber } = this.props;

    if (prevCallState !== callState) {
      if(callState !== CallStateEnum.INCOMING){
       this.ipcRenderer?.emit(ipcConstants.CLOSE_INCOMING_CALL_NOTIFICATION);
      }
      switch (callState) {
        case CallStateEnum.CONNECTING:
          this.clearIndex();
          clearInterval(this.timer);
          this.timer = setInterval(this.increaseIndex, 500);
          break;
        case CallStateEnum.CONNECTED:
          clearInterval(this.timer);
          this.timer = setInterval(this.setTimestamp, 500);
          break;
        case CallStateEnum.INCOMING:
          this.audioPlayer.play();
          this.ipcRenderer?.emit(ipcConstants.OPEN_INCOMING_CALL_NOTIFICATION, incomingCallNumber, dialedNumber);
          this.clearIndex();
          clearInterval(this.timer);
          this.timer = setInterval(this.increaseIndex, 500);
          break;
        case CallStateEnum.ACCEPT:
          this.audioPlayer.pause();
          clearInterval(this.timer);
          break;
        default:
          this.audioPlayer.pause();
          clearInterval(this.timer);
          break;
      }
    }

    if (prevSelectedCallNotificationSound !== selectedCallNotificationSound) {
      if (selectedCallNotificationSound !== -1) {
        const filePath = new FilePathHelper().getFullFilePath(
          `sounds/${uiString.CALL_NOTIFICATION_SOUND_FILES[selectedCallNotificationSound]}`
        );
        this.audioPlayer.pause();
        this.audioPlayer.src = filePath;
      }
      else {
        this.audioPlayer.pause();
        this.audioPlayer.src = "";
      }
    }
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);

    this.audioPlayer.pause();
  }

  render() {
    return this.showCallStatus();
  }
}

// Redux data binding
const mapStateToProps = (state) => ({
  callNumber: state.callReducer.callNumber,
  incomingCallNumber: state.callReducer.incomingCallNumber,
  dialedNumber: state.callReducer.dialedNumber,
  numberToCall: state.callReducer.numberToCall,
  connectedTime: state.callReducer.connectedTime,
  callState: state.callReducer.callState,
  isMuteMic: state.callReducer.isMuteMic,
  callType: state.callReducer.callType,
  selectedCallNotificationSound:
    state.preferences.selectedCallNotificationSound,
});

export default connect(mapStateToProps, { hangUp, setIsMuteMic, accept})(
  CallStatus
);
